import React, { createContext, ReactNode, useContext, useState } from 'react';
import { Tenant } from '@restore/services-lib';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { CREATE_TENANT } from 'graphql/tenant/mutations/createTenants.gql';
import { UPDATE_TENANT } from 'graphql/tenant/mutations/updateTenant.gql';
import { GET_TENANTS } from 'graphql/tenant/queries/getTenants.gql';
import { UPDATE_TENANT_CONFIG } from 'graphql/tenant/mutations/updateTenantConfig.gql';
import { validateEntityName } from 'const/regex';
import { useSnackbarContext } from 'context/snackbar.context';

export interface CreateTenantInterface {
  name: string;
}

export interface UpdateTenantInterface extends CreateTenantInterface {
  id: number;
}

export interface TenantsFilter {
  name?: string;
  storeStateName?: string;
}

export interface UpdateTenantConfigInterface {
  id: number;
  name: string;
  dateFormat: string;
  pillar1Name: string;
  pillar2Name: string;
  pillar3Name: string;
  pillar4Name: string;
  storeRef1Name: string;
  storeRef2Name: string;
  storeRef3Name: string;
  storeStateName: string;
}

interface TenantsContextInterface {
  tenants: Tenant[];
  setTenants: (tenants: Tenant[]) => void;
  tenantsFilter: TenantsFilter;
  setTenantsFilter: (filter: TenantsFilter) => void;
  addTenant: (tenant: CreateTenantInterface) => Promise<void>;
  editTenant: (tenant: UpdateTenantInterface) => Promise<void>;
  editTenantConfig: (tenant: UpdateTenantConfigInterface) => Promise<void>;
  loading: boolean;
  editTenantLoading: boolean;
  createTenantLoading: boolean;
  error?: ApolloError;
}

const defaultContext: TenantsContextInterface = {
  tenants: [],
  setTenants: () => {},
  tenantsFilter: {},
  setTenantsFilter: () => {},
  addTenant: async () => {},
  editTenant: async () => {},
  editTenantConfig: async () => {},
  loading: false,
  editTenantLoading: false,
  createTenantLoading: false,
};

const TenantsContext = createContext<TenantsContextInterface>(defaultContext);

export const TenantsContextProvider = ({ children }: { children: ReactNode }) => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [createTenant] = useMutation(CREATE_TENANT);
  const [updateTenant, { loading: updateTenantLoading }] = useMutation(UPDATE_TENANT);
  const [updateTenantConfig, { loading: updateTenantConfigLoading }] =
    useMutation(UPDATE_TENANT_CONFIG);
  const editTenantLoading = updateTenantLoading || updateTenantConfigLoading;
  const [createTenantLoading, setCreateTenantLoading] = useState(false); // creating a tenant takes time to go through aws
  const { displaySnackbarMessage, displayApiResult } = useSnackbarContext();
  const [tenantsFilter, setTenantsFilter] = useState<TenantsFilter>({});

  // fetch tenants
  const { loading, error, refetch } = useQuery(GET_TENANTS, {
    variables: {
      tenantsFilter,
    },
    onCompleted: (data) => {
      setTenants(data.tenants);
    },
  });

  // this is an EXTRA check. validation is being handled upon name entry. in case that gets accidentally changed, this will prevent submission
  const handleValidateName = (name?: string | null) => {
    if (!name || name.length < 2 || !validateEntityName(name)) {
      return false;
    }
    return true;
  };

  const addTenant = async (input: CreateTenantInterface) => {
    setCreateTenantLoading(true);
    if (handleValidateName(input.name)) {
      await createTenant({
        variables: {
          input,
        },
      }).then(
        (result) => {
          displayApiResult(result.data.createTenant, 'Created successfully');
        },
        (rejection) => {
          displaySnackbarMessage(rejection.message, 'error');
        }
      );
      refetch();
      setCreateTenantLoading(false);
    }
  };

  const editTenant = async (input: UpdateTenantInterface) => {
    await updateTenant({
      variables: {
        input,
      },
    }).then(
      (result) => {
        displayApiResult(result.data.updateTenant, 'Updated successfully');
      },
      (rejection) => {
        displaySnackbarMessage(rejection.message, 'error');
      }
    );
    refetch();
  };

  const editTenantConfig = async (input: UpdateTenantConfigInterface) => {
    await updateTenantConfig({
      variables: {
        input,
      },
    }).then(
      (result) => {
        displayApiResult(result.data.updateTenantConfig, 'Updated successfully');
      },
      (rejection) => {
        displaySnackbarMessage(rejection.message, 'error');
      }
    );
    refetch();
  };

  return (
    <TenantsContext.Provider
      value={{
        tenants,
        setTenants,
        tenantsFilter,
        setTenantsFilter,
        addTenant,
        editTenant,
        editTenantConfig,
        loading,
        createTenantLoading,
        editTenantLoading,
        error,
      }}
    >
      {children}
    </TenantsContext.Provider>
  );
};

export const useTenantsContext = () => useContext(TenantsContext);
